import { supabase } from "@/platform/lib/supabase";
import type {
  PlatformTenantSlug,
  PlatformWaitlistRow,
  PlatformWaitlistStatsRow,
} from "@/platform/lib/database.types";

/**
 * Inscreve a usuária autenticada na lista de espera de um tenant em status
 * 'waitlist'. Idempotente — se já estava na lista, retorna a linha existente.
 *
 * Também marca `platform_profiles.default_tenant` para esse tenant, para que
 * o redirect pós-login leve a usuária para o produto certo.
 */
export async function joinWaitlist(tenant: PlatformTenantSlug): Promise<PlatformWaitlistRow> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) {
    throw new Error("Sessão inválida — faça login antes.");
  }
  const userId = userData.user.id;

  const { data, error } = await supabase
    .from("platform_waitlist")
    .upsert(
      { user_id: userId, tenant },
      { onConflict: "user_id,tenant", ignoreDuplicates: false },
    )
    .select()
    .single();
  if (error) throw error;

  await supabase
    .from("platform_profiles")
    .update({ default_tenant: tenant })
    .eq("user_id", userId);

  // Email de boas-vindas (best-effort, ignora falha).
  if (tenant === "usp") {
    void sendWaitlistWelcomeEmail();
  }

  return data as PlatformWaitlistRow;
}

async function sendWaitlistWelcomeEmail(): Promise<void> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) return;
    await fetch("/api/email/waitlist-welcome", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ tenant: "usp" }),
    });
  } catch (err) {
    console.error("[waitlist] email-welcome failed", err);
  }
}

/**
 * Retorna a entrada da usuária na lista de espera daquele tenant, ou null.
 */
export async function getWaitlistEntry(
  tenant: PlatformTenantSlug,
): Promise<PlatformWaitlistRow | null> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return null;

  const { data, error } = await supabase
    .from("platform_waitlist")
    .select()
    .eq("user_id", userData.user.id)
    .eq("tenant", tenant)
    .maybeSingle();
  if (error) throw error;
  return (data as PlatformWaitlistRow | null) ?? null;
}

/**
 * Sinaliza intenção de pré-pagamento (Gate 1 do FuvestPro). Não cobra de
 * fato — apenas registra interesse para que admin contate a usuária quando
 * o checkout abrir.
 */
export async function togglePaymentIntent(
  tenant: PlatformTenantSlug,
  intent: boolean,
  amountBrl?: number,
): Promise<PlatformWaitlistRow> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) {
    throw new Error("Sessão inválida — faça login antes.");
  }

  const { data, error } = await supabase
    .from("platform_waitlist")
    .update({
      signal_payment_intent: intent,
      signal_amount_brl: intent ? (amountBrl ?? null) : null,
    })
    .eq("user_id", userData.user.id)
    .eq("tenant", tenant)
    .select()
    .single();
  if (error) throw error;

  // Email de confirmação (best-effort) quando passou para true.
  if (intent && tenant === "usp") {
    void sendPaymentIntentEmail();
  }

  return data as PlatformWaitlistRow;
}

async function sendPaymentIntentEmail(): Promise<void> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) return;
    await fetch("/api/email/payment-intent", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ tenant: "usp" }),
    });
  } catch (err) {
    console.error("[waitlist] email-payment-intent failed", err);
  }
}

/**
 * Estatísticas agregadas da lista de espera (admin-only via RLS).
 */
export async function getWaitlistStats(): Promise<PlatformWaitlistStatsRow[]> {
  const { data, error } = await supabase
    .from("platform_waitlist_stats")
    .select();
  if (error) throw error;
  return (data ?? []) as PlatformWaitlistStatsRow[];
}

/**
 * Lista todas as entradas da waitlist com display_name/email do auth.users
 * via join (admin-only — RLS permite quando platform_is_admin()).
 */
export interface WaitlistEntryWithUser extends PlatformWaitlistRow {
  display_name: string | null;
  full_name: string | null;
}

export async function adminListWaitlist(
  tenant?: PlatformTenantSlug,
): Promise<WaitlistEntryWithUser[]> {
  let query = supabase
    .from("platform_waitlist")
    .select(`
      id, user_id, tenant, signal_payment_intent, signal_amount_brl,
      notes, created_at, updated_at, metadata,
      platform_profiles!inner(display_name, full_name)
    `)
    .order("created_at", { ascending: true });
  if (tenant) query = query.eq("tenant", tenant);

  const { data, error } = await query;
  if (error) throw error;

  return (data ?? []).map((row) => {
    const r = row as unknown as PlatformWaitlistRow & {
      platform_profiles:
        | { display_name: string | null; full_name: string | null }
        | { display_name: string | null; full_name: string | null }[]
        | null;
    };
    const profile = Array.isArray(r.platform_profiles)
      ? r.platform_profiles[0]
      : r.platform_profiles;
    return {
      ...r,
      display_name: profile?.display_name ?? null,
      full_name: profile?.full_name ?? null,
    } as WaitlistEntryWithUser;
  });
}

/**
 * Posição da usuária na fila de um tenant (ordenado por created_at asc).
 * Retorna 1-indexed.
 */
export async function getWaitlistPosition(
  tenant: PlatformTenantSlug,
): Promise<{ position: number | null; total: number }> {
  const entry = await getWaitlistEntry(tenant);
  if (!entry) return { position: null, total: 0 };

  const { count: total } = await supabase
    .from("platform_waitlist")
    .select("*", { count: "exact", head: true })
    .eq("tenant", tenant);

  const { count: ahead } = await supabase
    .from("platform_waitlist")
    .select("*", { count: "exact", head: true })
    .eq("tenant", tenant)
    .lt("created_at", entry.created_at ?? new Date().toISOString());

  return {
    position: (ahead ?? 0) + 1,
    total: total ?? 0,
  };
}
